import { useState, useEffect } from "react";
import { Check, Clock, DollarSign } from "lucide-react";
import { cn } from "@/lib/core/utils";

// Individual step components with their own animations
interface StepProps {
  isActive: boolean;
  isCompleted: boolean;
  isHovered: boolean;
  currentStep?: number;
}

const Step1CreateShift = ({ isActive, isCompleted, isHovered }: StepProps) => {
  const shouldAnimate = isActive && isHovered;
  const times = ["8am", "9am", "10am", "11am", "12pm", "1pm", "2pm"];

  return (
    <div className="relative h-full flex items-center justify-center overflow-hidden">
      {/* Calendar grid with vertical lines and time labels - always visible */}
      <div className="absolute inset-0 px-1 pt-3 pb-2">
          {/* Time labels at top */}
          <div className="absolute top-0 inset-x-0 grid grid-cols-7 gap-0 px-1">
            {times.map((time, i) => (
              <div key={i} className="text-[6px] text-foreground/40 text-center font-medium">
                {time}
              </div>
            ))}
          </div>

          {/* Vertical lines */}
          <div className="absolute inset-x-0 top-3 bottom-0 grid grid-cols-7 gap-0 px-1">
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                className="border-r border-foreground/10 last:border-r-0"
              />
            ))}
          </div>
        </div>

      {/* Grab cursor indicator - follows the drag path */}
      {shouldAnimate && (
        <div
          className="absolute left-1/2 w-4 h-4 workflow-cursor-drag pointer-events-none"
          style={{ top: 'calc(50% + 6px)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-foreground/60">
            <path d="M9 3v1.5M15 3v1.5M9 19.5V21M15 19.5V21M3 9h1.5M3 15h1.5M19.5 9H21M19.5 15H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <rect x="7" y="7" width="10" height="10" stroke="currentColor" strokeWidth="2" rx="1"/>
          </svg>
        </div>
      )}
    </div>
  );
};

const StepSelectEmployee = ({ isActive, isCompleted, isHovered }: StepProps) => {
  const employees = [
    { initials: "JD", name: "John D.", color: "blue" },
    { initials: "AS", name: "Anna S.", color: "purple" },
    { initials: "MK", name: "Mike K.", color: "green" },
  ];

  return (
    <div className="relative h-full flex items-center justify-center gap-3 px-2">
      {/* 3 employee avatars in a row */}
      {employees.map((emp, i) => (
        <div key={emp.initials} className="flex flex-col items-center gap-1">
          <div
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500",
              emp.color === "blue" && "bg-blue-500/20 text-blue-600 border-2 border-blue-500/40",
              emp.color === "purple" && "bg-primary/20 text-primary border-2 border-primary/40",
              emp.color === "green" && "bg-emerald-500/20 text-emerald-600 border-2 border-emerald-500/40",
              // First employee (JD) animates away when active - high z-index so it flies above other elements
              i === 0 && isActive && isHovered && "workflow-employee-fly-out z-50",
              // After completed, JD is gone (faded)
              i === 0 && isCompleted && "opacity-30"
            )}
          >
            {emp.initials}
          </div>
          <span className="text-[8px] text-foreground/60">{emp.name}</span>
        </div>
      ))}
    </div>
  );
};

const Step2AssignStaff = ({ isActive, isCompleted, isHovered }: StepProps) => (
  <div className="relative h-full flex items-center gap-3 px-2">
    <div
      className={cn(
        "flex-1 h-10 rounded-lg px-3 shadow-md flex items-center justify-between transition-all duration-500",
        isCompleted ? "bg-blue-500/70" : "bg-gray-400/30"
      )}
    >
      <span className="text-xs font-semibold text-white">Morning Shift</span>
      <span className="text-xs text-white/80">09-17</span>
    </div>
    <div
      className={cn(
        "w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-sm font-bold text-blue-600 border-2 border-blue-500 transition-all duration-500 shadow-md",
        isActive && isHovered && "workflow-avatar-drop",
        !isActive && !isCompleted && "opacity-0 scale-0"
      )}
    >
      JD
    </div>
  </div>
);

const Step3ClockIn = ({ isActive, isCompleted, isHovered }: StepProps) => (
  <div className="relative h-full flex items-center gap-3 px-2">
    <div className="w-8 h-8 rounded-lg bg-foreground/10 flex items-center justify-center flex-shrink-0">
      <div className="grid grid-cols-3 gap-px">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="w-1 h-1 bg-foreground/60 rounded-sm" />
        ))}
      </div>
    </div>
    <div
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/20 text-green-600 transition-all duration-500",
        isActive && isHovered && "workflow-fade-in-scale",
        !isActive && !isCompleted && "opacity-0 scale-75"
      )}
    >
      <div className="w-1 h-1 rounded-full bg-green-500" />
      <span className="text-[10px] font-medium">JD Clocked In</span>
      <span className="text-[8px] text-green-500/80">On Time</span>
    </div>
  </div>
);

const Step4TrackHours = ({ isActive, isCompleted, isHovered }: StepProps) => (
  <div className="relative h-full flex items-center gap-3 px-2">
    <Clock className="w-8 h-8 text-foreground/70 flex-shrink-0" />
    <div className="flex-1">
      <span className="text-lg font-bold text-foreground tabular-nums block">8h 15m</span>
      <div className="w-full h-2 rounded-full bg-foreground/10 overflow-hidden mt-1">
        <div
          className={cn(
            "h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out",
            (isActive || isCompleted) && isHovered ? "workflow-progress-fill" : "w-0"
          )}
        />
      </div>
    </div>
  </div>
);

const Step5CalculatePay = ({ isActive, isCompleted, isHovered }: StepProps) => (
  <div className="relative h-full flex items-center gap-3 px-2">
    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500/20 flex-shrink-0">
      <DollarSign className="w-6 h-6 text-orange-500" />
    </div>
    <span
      className={cn(
        "text-xl font-bold text-orange-500 tabular-nums transition-all duration-500",
        isActive && isHovered && "workflow-fade-in-scale",
        !isActive && !isCompleted && "opacity-0 scale-75"
      )}
    >
      €156.25
    </span>
  </div>
);

const Step6PayslipReady = ({ isActive, isCompleted, isHovered }: StepProps) => (
  <div className="relative h-full flex items-center gap-3 px-2">
    <div
      className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center bg-green-500 transition-all duration-500",
        isActive && isHovered && "workflow-checkmark-pop",
        !isActive && !isCompleted && "opacity-0 scale-0"
      )}
    >
      <Check className="w-6 h-6 text-white" />
    </div>
    <div className="flex flex-col">
      <span className="text-sm font-semibold text-foreground">Payslip Ready</span>
      <span className="text-xs text-foreground/60">Sent automatically</span>
    </div>
  </div>
);

// Main workflow component
export const WorkflowPhoneMockup = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const steps = [
    { num: 1, title: "Create Shift", desc: "Drag to schedule", component: Step1CreateShift },
    { num: 2, title: "Assign Employee", desc: "Drag to shift", component: StepSelectEmployee },
    { num: 3, title: "Clock In", desc: "QR scan attendance", component: Step3ClockIn },
    { num: 4, title: "Track Hours", desc: "Real-time tracking", component: Step4TrackHours },
    { num: 5, title: "Calculate Pay", desc: "Auto payroll", component: Step5CalculatePay },
    { num: 6, title: "Payslip Ready", desc: "Sent automatically", component: Step6PayslipReady },
  ];

  // Auto-play animation sequence only when hovered
  useEffect(() => {
    if (!isHovered) {
      setCurrentStep(0); // Reset to first step when not hovered
      return;
    }

    // Different timing for different steps
    const getStepDuration = () => {
      if (currentStep === 0) return 2500; // Create shift - 2.5s for drag animation
      if (currentStep === 1) return 2800; // Assign employee - 2s animation + 0.8s pause after landing
      return 1500; // Other steps - 1.5s
    };

    const timer = setTimeout(() => {
      setCurrentStep((prev) => {
        const nextStep = prev < steps.length - 1 ? prev + 1 : 0;
        return nextStep;
      });
    }, getStepDuration());

    return () => clearTimeout(timer);
  }, [steps.length, isHovered, currentStep]);

  // Determine shift background color based on workflow progress
  const getShiftBgColor = () => {
    // During step 0 animation, let CSS handle it (stays orange)
    if (currentStep === 0) return undefined;
    // Step 1: Still dragging employee - stays orange
    if (currentStep === 1) return 'rgb(249 115 22 / 0.7)'; // orange-500/70 - still assigning
    // Step 2: Employee assigned, now blue
    if (currentStep === 2) return 'rgb(59 130 246 / 0.7)'; // blue-500/70 - assigned
    // Step 3: After clock in, change to green
    if (currentStep === 3) return 'rgb(16 185 129 / 0.7)'; // emerald-500/70
    // Step 4: Track hours - slightly darker green
    if (currentStep === 4) return 'rgb(5 150 105 / 0.75)'; // emerald-600/75
    // Calculate pay and beyond - even darker green
    return 'rgb(22 163 74 / 0.8)'; // green-600/80
  };

  const shouldAnimateShift = currentStep === 0 && isHovered;

  return (
    <div
      className="relative w-full h-full flex flex-col p-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main header at top */}
      <div className="mb-4">
        <h3 className="bento-feature-title text-foreground">The Workflow</h3>
        <p className="bento-feature-subtitle mt-0.5">You schedule, we handle the rest.</p>
      </div>

      {/* All steps visible vertically */}
      <div className="flex-1 flex flex-col justify-center gap-4 relative">
        {steps.map((step, index) => {
          const StepComponent = step.component;
          const isActive = currentStep === index;
          const isCompleted = currentStep > index;

          return (
            <div
              key={step.num}
              className={cn(
                "relative transition-all duration-300",
                isActive || isCompleted ? "opacity-100 scale-100" : "opacity-60 scale-95"
              )}
            >
              {/* Step content - allow overflow for employee selection step so avatar can fly out */}
              <div className={cn(
                "h-14 rounded-lg bg-background/50",
                index === 1 ? "overflow-visible" : "overflow-hidden"
              )}>
                <StepComponent isActive={isActive} isCompleted={isCompleted} isHovered={isHovered} currentStep={currentStep} />
              </div>
            </div>
          );
        })}

        {/* Persistent shift element - stays visible throughout all steps */}
        {isHovered && (
          <div
            className={cn(
              "absolute left-1/2 h-9 rounded-lg px-2 shadow-md flex items-center gap-1.5 z-10",
              shouldAnimateShift && "workflow-shift-unified",
              // Step 1: animate color from orange to blue when employee lands
              currentStep === 1 && isHovered && "workflow-shift-assign"
            )}
            style={{
              top: 'calc(50% - 11.25rem)', // Align with first step center (6 steps centered vertically)
              backgroundColor: currentStep > 1 ? getShiftBgColor() : undefined,
              transform: shouldAnimateShift ? undefined : 'translate(-50%, -50%)'
            }}
          >
            {/* Shift info */}
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-[9px] font-semibold text-white leading-tight">Morning</span>
              <span className="text-[7px] text-white/80 leading-tight">09:00-17:00</span>
            </div>

            {/* Employee avatar placeholder - dashed circle from start, fills when employee lands */}
            <div className={cn(
              "w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0",
              // Step 0-1: dashed empty circle waiting for employee
              currentStep <= 1 && "border-2 border-dashed border-white/50",
              // Step 1: animate the fill at end of step
              currentStep === 1 && isHovered && "workflow-avatar-fly-in",
              // Step 2+: solid filled circle
              currentStep > 1 && "workflow-avatar-container-completed"
            )}>
              <span className={cn(
                "opacity-0",
                currentStep === 1 && isHovered && "workflow-avatar-text-fly-in",
                currentStep > 1 && "workflow-avatar-text-completed"
              )}>JD</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
